import * as React from "react";
import { useRouter } from "next/router";
import { useMachine } from "@xstate/react";
import { IPostpaidOffer } from "@nju/data-access-portal-offers/src/fetchProductOffers/types";
import { useUserLoginState } from "@nju/data-access-portal-user";
import { IconName } from "@nju/icons";
import { portalRoutes } from "@nju/routes";
import { Button, Heading, IconListElement, Separator, Text } from "@nju/ui";
import { postpaidOfferDetailsMachine } from "./postpaidOfferDetails.machine";

interface IPostpaidOfferDetailsProps {
  offer: IPostpaidOffer;
}

export function PostpaidOfferDetails({ offer }: IPostpaidOfferDetailsProps) {
  const router = useRouter();
  const { isLoggedIn } = useUserLoginState();

  const {
    offerId,
    shortFeatures,
    extraShortFeatures,
    label,
    paymentFeatures,
    discountFeatures,
    bonusFeatures,
  } = offer;

  const [current, send] = useMachine(postpaidOfferDetailsMachine, {
    actions: {
      onSubmit: (_, event) => {
        if (event.type !== "SUCCESS") {
          return;
        }

        if (!isLoggedIn) {
          return router.push(
            portalRoutes.zalozKonto.get({
              oferta: offerId,
              mode: "postpaid",
            }),
          );
        }

        return router.push(portalRoutes.wybierak.get({ offerId }));
      },
      onAnotherOrderExists: () => {
        router.push(portalRoutes.zamowienieWTrakcie.get());
      },
      onNoPesel: () => {
        router.push(portalRoutes.brakNumeruPesel.get());
      },
      onUnder18: () => {
        router.push(portalRoutes.under18.get());
      },
      onTechnicalVerificationPending: () => {
        router.push(portalRoutes.technicalVerificationPending.get());
      },
    },
  });

  const handleSubmit = React.useCallback(() => {
    if (isLoggedIn) {
      send({ type: "SUBMIT" });
    } else {
      router.push(
        portalRoutes.zalozKonto.get({ oferta: offerId, mode: "postpaid" }),
      );
    }
  }, [isLoggedIn, offerId, router, send]);

  return (
    <>
      <Separator size="xs" tone="invisible" />
      <div className="flex items-center justify-between">
        <Text
          align="right"
          font="ttfirs"
          size={{
            "@onlyMobile": "m",
            "@fromTablet": "l",
            "@fromDesktop": "keyM",
          }}
          tone="accent"
          weight="bold"
        >
          nju abonament
        </Text>
        <div className="flex items-center">
          <span className="text-size-16 font-bold text-black bg-ripeLemon py-[9.5px] px-[8.5px] rounded-[8px] mr-1">
            eSIM
          </span>
          {label === "5G" && (
            <span className="text-size-16 font-bold text-white bg-daintree py-[9.5px] px-[8.5px] rounded-[8px]">
              {label}
            </span>
          )}
        </div>
      </div>
      <Separator size="xs" tone="invisible" />
      <div className="w-[229px]">
        <Button
          display="block"
          isLoading={!current.matches("start")}
          onPress={handleSubmit}
          size={{
            "@onlyMobile": "s",
            "@fromTablet": "m",
          }}
        >
          wybierz
        </Button>
      </div>
      <Separator size="s" tone="invisible" />
      {paymentFeatures && (
        <>
          <Heading font="ttfirs" tone="neutral" variant="h4" weight="bold">
            opłaty
          </Heading>
          <Separator size="s" thickness="medium" tone="accent" />
          {paymentFeatures.map((feature) => {
            return (
              <IconListElement
                iconAlign="start"
                iconName={feature.icon as IconName}
                iconSize="medium"
                key={feature.name}
                mode="markdown"
                text={feature.description.pl}
              />
            );
          })}
        </>
      )}
      <Separator size="xs" tone="invisible" />
      <Heading font="ttfirs" tone="neutral" variant="h4" weight="bold">
        rabaty
      </Heading>
      <Separator size="xs" thickness="medium" tone="accent" />
      <Separator size="xxs" tone="invisible" />
      {discountFeatures?.map((feature) => {
        //const check if feature name has partly suffix "_HIGHLIGHT"
        const isDashedBorder = feature.name.endsWith("_HIGHLIGHT");
        return (
          <React.Fragment key={feature.name}>
            {isDashedBorder && (
              <IconListElement
                borderStyle="dashed"
                darkTooltipIcon={true}
                iconName={feature.icon as IconName}
                iconSize="medium"
                mode="markdown"
                testId="discounts-info-box"
                text={feature.description.pl}
                tooltipContent={feature.additionalDescription?.pl}
              />
            )}
          </React.Fragment>
        );
      })}
      {bonusFeatures && (
        <>
          <Separator size="xs" tone="invisible" />
          <Heading font="ttfirs" tone="neutral" variant="h4" weight="bold">
            bonusy
          </Heading>
          <Separator size="xs" thickness="medium" tone="accent" />
          <Separator size="xxs" tone="invisible" />
          {bonusFeatures?.map((feature) => {
            //const check if feature name has partly suffix "_HIGHLIGHT"
            const isDashedBorder = feature.name.endsWith("_HIGHLIGHT");
            return (
              <React.Fragment key={feature.name}>
                {isDashedBorder && (
                  <IconListElement
                    borderStyle="dashed"
                    darkTooltipIcon={true}
                    iconName={feature.icon as IconName}
                    iconSize="medium"
                    mode="markdown"
                    text={feature.description.pl}
                    tooltipContent={feature.additionalDescription?.pl}
                  />
                )}
              </React.Fragment>
            );
          })}
        </>
      )}
      <Separator size="xs" tone="invisible" />
      <Heading font="ttfirs" tone="neutral" variant="h4" weight="bold">
        oferta
      </Heading>
      <Separator size="s" thickness="medium" tone="accent" />
      {shortFeatures.map((feature) => {
        return (
          <IconListElement
            iconAlign="start"
            iconName={feature.icon as IconName}
            iconSize="big"
            key={feature.name}
            mode="markdown"
            text={feature.description.pl}
          />
        );
      })}
      {extraShortFeatures && (
        <>
          <Separator size="xs" tone="invisible" />
          <Heading font="ttfirs" tone="neutral" variant="h4" weight="bold">
            dodatkowo
          </Heading>
          <Separator size="s" />
          {extraShortFeatures.map((extras, index) => {
            return (
              <IconListElement
                iconAlign="center"
                iconName={extras.icon as IconName}
                iconSize="medium"
                key={index}
                mode="markdown"
                text={extras.description.pl}
              />
            );
          })}
        </>
      )}
    </>
  );
}
